import xior, {type XiorError, type XiorRequestConfig, type XiorResponse} from 'xior';
import jsGlobalConfig from '../../../globals/jsGlobalConfig';

type Method =
  | 'get' | 'GET'
  | 'delete' | 'DELETE'
  | 'head' | 'HEAD'
  | 'options' | 'OPTIONS'
  | 'post' | 'POST'
  | 'put' | 'PUT'
  | 'patch' | 'PATCH'
  | 'purge' | 'PURGE'
  | 'link' | 'LINK'
  | 'unlink' | 'UNLINK';

const httpClient = xior.create({
  timeout: 20000,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error: XiorError) => {
    const statusCode = error?.response?.status;

    if (statusCode === 408) {
      window.logJSError('timeout', null, null, null, error);
    }
    else if (statusCode === 403) {
      const config = error.config as XiorRequestConfig & { retryCount?: number };
      if (!config.retryCount || config.retryCount <= 2) {
        window.__globalConfig.csrfToken.value = await getNewToken();

        // There are 2 methods for passing the token. Header has priority, so we update only this.
        Object.assign(
          config.headers,
          jsGlobalConfig().getCsrfTokenHeader(),
          {retryCount: config.retryCount ? config.retryCount + 1 : 1}
        );

        // retry the request with new token
        return httpClient.request(config);
      }
    }

    return Promise.reject(error);
  }
);


async function getNewToken(): Promise<string> {
  const response = await httpClient.get('/token/');
  return response.data;
}


interface iHttpRequest {
  (url: string, config?: XiorRequestConfig): Promise<XiorResponse>;
  get: typeof httpClient.get;
  post: typeof httpClient.post;
  put: typeof httpClient.put;
  delete: typeof httpClient.delete;
  head: typeof httpClient.head;
}
const httpRequest: iHttpRequest = wrapper as iHttpRequest;
httpRequest.get = httpClient.get.bind(httpClient);
httpRequest.post = httpClient.post.bind(httpClient);
httpRequest.put = httpClient.put.bind(httpClient);
httpRequest.delete = httpClient.delete.bind(httpClient);
httpRequest.head = httpClient.head.bind(httpClient);

function wrapper(url: string, config?: XiorRequestConfig)  {
  const method = config?.method || 'GET';

  switch (method) {
    case 'GET':
    case 'get':
      return httpClient.get(url, config);
    case 'POST':
    case 'post':
      return httpClient.post(url, config.data, config);
    case 'PUT':
    case 'put':
      return httpClient.put(url, config.data, config);
    case 'DELETE':
    case 'delete':
      return httpClient.delete(url, config);
    case 'HEAD':
    case 'head':
      return httpClient.head(url, config);
    case 'OPTIONS':
    case 'options':
      return httpClient.options(url, config);
    case 'PATCH':
    case 'patch':
      return httpClient.patch(url, config);
    default:
      throw new Error(`Unsupported method: ${method}`);
  }
}

export default httpRequest;
export {Method}
